import {
  Room,
  RoomEvent,
  Track,
  type LocalTrackPublication,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
} from 'livekit-client';
import type { CallBootstrap } from '@domain/call/types';
import type { RealtimeConversationRef, RealtimeSessionKind } from '@domain/realtime/types';
import { fetchLiveKitToken } from './api';

export type LiveKitMediaSession = {
  mediaCapability: string;
  kind: Extract<RealtimeSessionKind, 'group-audio' | 'group-video'>;
};

export type LiveKitRemoteStream = {
  userId: number;
  stream: MediaStream;
};

type LiveKitMediaRoomOptions = {
  apiBase: string;
  bootstrap: CallBootstrap;
  currentUserName: string;
  onLocalStream: (stream: MediaStream | null) => void;
  onRemoteStreams: (streams: LiveKitRemoteStream[]) => void;
};

export class LiveKitMediaRoomController {
  private room: Room | null = null;
  private readonly remoteStreams = new Map<number, MediaStream>();

  constructor(private readonly options: LiveKitMediaRoomOptions) {}

  async join(session: LiveKitMediaSession) {
    await this.disconnect();
    const token = await fetchLiveKitToken(this.options.apiBase, {
      mediaCapability: session.mediaCapability,
      deviceId: this.options.bootstrap.deviceId,
      platform: this.options.bootstrap.platform,
      displayName: this.options.currentUserName,
    });

    const room = new Room({
      // Keep these disabled until Web and Flutter both render through native
      // LiveKit attachment paths; earlier optimization attempts froze remote video.
      adaptiveStream: false,
      dynacast: false,
    });
    this.room = room;
    room
      .on(RoomEvent.TrackSubscribed, (
        track: RemoteTrack,
        _publication: RemoteTrackPublication,
        participant: RemoteParticipant,
      ) => {
        this.upsertRemoteTrack(participant, track);
      })
      .on(RoomEvent.TrackUnsubscribed, (
        track: RemoteTrack,
        _publication: RemoteTrackPublication,
        participant: RemoteParticipant,
      ) => {
        this.removeRemoteTrack(participant, track);
      })
      .on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
        const userId = this.userIdFromIdentity(participant.identity);
        if (userId === null) {
          return;
        }
        this.remoteStreams.delete(userId);
        this.emitRemoteStreams();
      })
      .on(RoomEvent.LocalTrackPublished, () => this.emitLocalStream(room))
      .on(RoomEvent.LocalTrackUnpublished, () => this.emitLocalStream(room));

    await room.connect(token.livekitUrl, token.token);
    if (session.kind === 'group-video') {
      await room.localParticipant.enableCameraAndMicrophone();
    } else {
      await room.localParticipant.setMicrophoneEnabled(true);
    }
    this.emitLocalStream(room);
    room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((publication) => {
        if (publication.kind === Track.Kind.Audio || publication.kind === Track.Kind.Video) {
          this.upsertRemoteTrack(participant, publication.track);
        }
      });
    });
    return token.roomName;
  }

  async disconnect() {
    const room = this.room;
    this.room = null;
    this.remoteStreams.clear();
    this.options.onRemoteStreams([]);
    this.options.onLocalStream(null);
    await room?.disconnect();
  }

  private emitLocalStream(room: Room) {
    const tracks = Array.from(room.localParticipant.trackPublications.values())
      .map((publication: LocalTrackPublication) => publication.track?.mediaStreamTrack)
      .filter((track): track is MediaStreamTrack => Boolean(track));
    this.options.onLocalStream(tracks.length > 0 ? new MediaStream(tracks) : null);
  }

  private upsertRemoteTrack(participant: RemoteParticipant, track?: RemoteTrack) {
    const userId = this.userIdFromIdentity(participant.identity);
    if (userId === null || !track?.mediaStreamTrack) {
      return;
    }
    const stream = this.remoteStreams.get(userId) ?? new MediaStream();
    if (!stream.getTracks().some((existingTrack) => existingTrack.id === track.mediaStreamTrack.id)) {
      stream.addTrack(track.mediaStreamTrack);
    }
    this.remoteStreams.set(userId, stream);
    this.emitRemoteStreams();
  }

  private removeRemoteTrack(participant: RemoteParticipant, track?: RemoteTrack) {
    const userId = this.userIdFromIdentity(participant.identity);
    if (userId === null || !track?.mediaStreamTrack) {
      return;
    }
    const stream = this.remoteStreams.get(userId);
    if (!stream) {
      return;
    }
    stream.removeTrack(track.mediaStreamTrack);
    if (stream.getTracks().length === 0) {
      this.remoteStreams.delete(userId);
    }
    this.emitRemoteStreams();
  }

  private emitRemoteStreams() {
    this.options.onRemoteStreams(
      Array.from(this.remoteStreams.entries()).map(([userId, stream]) => ({ userId, stream })),
    );
  }

  private userIdFromIdentity(identity: string) {
    // Business issues identities as locs-user-<userId>-leg-<legId>.
    const userId = Number(identity.split('-')[2]);
    return Number.isFinite(userId) ? userId : null;
  }
}
